/**
 * Issue #1317 (Pick XI coach 2 of 3; D297, D300, D302) — the state model for
 * Pick the XI, step 1 of 2. Pure functions: no React, no network. A reducer
 * takes a state and returns a new one; a refused move (a full XI, a player
 * who is not in the squad) returns the state it was given.
 *
 * Shape:
 *   {
 *     squad:      [{ player_id, full_name }]           the game's squad, as the API sent it
 *     xi:         [{ id, captain, vice, keeper }]      batting order; index + 1 is the place
 *     twelfth:    id | null                            fields only
 *     bowling:    [id]                                 bowling order; 1 and 2 open
 *     volunteers: { scorer, umpire_1, umpire_2 }       free text, the coach's own view (D300)
 *   }
 *
 * The bench is not stored: it is the squad minus the XI minus the 12th man,
 * so a player who leaves the XI or the 12th spot is back on it with nothing
 * to keep in step. The batting order is dense (1..n). Removing a batter
 * closes the gap, and the bowling order loses them too. The screen draws one
 * "Not filled" slot after the last batter while the XI is short.
 */

/** Places in the XI. */
export const XI_SIZE = 11;

const VOLUNTEER_KEYS = ["scorer", "umpire_1", "umpire_2"];

const inSquad = (state, id) => state.squad.some((m) => m.player_id === id);
const inXi = (state, id) => state.xi.some((e) => e.id === id);

/**
 * The state a `GET /api/coach-games/:id` payload opens as: the draft sheet
 * when there is one, otherwise every squad player on the bench. A player the
 * draft names who is no longer in the squad is dropped, because the API
 * refuses one (`not_in_squad`) and the coach would be unable to go on.
 */
export function fromServer(game) {
  const squad = (game?.squad || []).map((m) => ({ player_id: m.player_id, full_name: m.full_name }));
  const volunteers = {
    scorer: game?.volunteers?.scorer || "",
    umpire_1: game?.volunteers?.umpire_1 || "",
    umpire_2: game?.volunteers?.umpire_2 || "",
  };
  const base = { squad, xi: [], twelfth: null, bowling: [], volunteers };
  const draft = game?.sheets?.draft;
  if (!draft) return base;

  const ids = new Set(squad.map((m) => m.player_id));
  const entries = [...(draft.xi || [])]
    .filter((e) => ids.has(e.player_id))
    .sort((a, b) => (a.bat_order ?? 99) - (b.bat_order ?? 99))
    .slice(0, XI_SIZE);
  const xi = entries.map((e) => ({ id: e.player_id, captain: !!e.captain, vice: !!e.vice_captain, keeper: !!e.keeper }));
  const bowling = entries
    .filter((e) => e.bowl_order != null)
    .sort((a, b) => a.bowl_order - b.bowl_order)
    .map((e) => e.player_id);
  const twelfth = draft.twelfth && ids.has(draft.twelfth.player_id) ? draft.twelfth.player_id : null;
  return { ...base, xi, twelfth, bowling };
}

/** The squad players who are neither in the XI nor the 12th man, in squad order. */
export function bench(state) {
  return state.squad.filter((m) => !inXi(state, m.player_id) && m.player_id !== state.twelfth);
}

/** True when the XI has all eleven places taken. */
export const isFull = (state) => state.xi.length >= XI_SIZE;

/** Bring a bench player into the next open place. Refused when the XI is full. */
export function addToXi(state, id) {
  if (isFull(state) || !inSquad(state, id) || inXi(state, id)) return state;
  const twelfth = state.twelfth === id ? null : state.twelfth;
  return { ...state, twelfth, xi: [...state.xi, { id, captain: false, vice: false, keeper: false }] };
}

/** Take the batter at place `n` (1-based) out. The order closes up and they leave the bowling. */
export function remove(state, n) {
  const entry = state.xi[n - 1];
  if (!entry) return state;
  return {
    ...state,
    xi: state.xi.filter((_, i) => i !== n - 1),
    bowling: state.bowling.filter((id) => id !== entry.id),
  };
}

function swap(list, a, b) {
  if (a < 0 || b < 0 || a >= list.length || b >= list.length) return list;
  const next = [...list];
  [next[a], next[b]] = [next[b], next[a]];
  return next;
}

/** Move the batter at place `n` one place up the order. The first place stays. */
export function moveUp(state, n) {
  if (n < 2 || n > state.xi.length) return state;
  return { ...state, xi: swap(state.xi, n - 1, n - 2) };
}

/** Move the batter at place `n` one place down the order. The last batter stays. */
export function moveDown(state, n) {
  if (n < 1 || n >= state.xi.length) return state;
  return { ...state, xi: swap(state.xi, n - 1, n) };
}

/**
 * Captain is a switch with one holder. Tapping it on another player moves it;
 * tapping it on the captain clears it. A player cannot be both captain and
 * vice-captain, so the tag taking the place of the other clears it.
 */
export function toggleCaptain(state, n) {
  const entry = state.xi[n - 1];
  if (!entry) return state;
  const on = !entry.captain;
  return { ...state, xi: state.xi.map((e, i) => (i === n - 1 ? { ...e, captain: on, vice: on ? false : e.vice } : { ...e, captain: false })) };
}

/** Vice-captain: the same rules as the captain, the other way round. */
export function toggleVice(state, n) {
  const entry = state.xi[n - 1];
  if (!entry) return state;
  const on = !entry.vice;
  return { ...state, xi: state.xi.map((e, i) => (i === n - 1 ? { ...e, vice: on, captain: on ? false : e.captain } : { ...e, vice: false })) };
}

/** Keeper is a switch on each player: one or more ("usually two"). */
export function toggleKeeper(state, n) {
  if (!state.xi[n - 1]) return state;
  return { ...state, xi: state.xi.map((e, i) => (i === n - 1 ? { ...e, keeper: !e.keeper } : e)) };
}

/** Name the 12th man from the bench. Replaces the one already named, who goes back to the bench. */
export function setTwelfth(state, id) {
  if (!inSquad(state, id) || inXi(state, id)) return state;
  return { ...state, twelfth: id };
}

export function clearTwelfth(state) {
  return state.twelfth === null ? state : { ...state, twelfth: null };
}

/** Add an XI player to the end of the bowling order. */
export function addBowler(state, id) {
  if (!inXi(state, id) || state.bowling.includes(id)) return state;
  return { ...state, bowling: [...state.bowling, id] };
}

/** Take the bowler at place `n` (1-based) out of the bowling order. They stay in the XI. */
export function removeBowler(state, n) {
  if (!state.bowling[n - 1]) return state;
  return { ...state, bowling: state.bowling.filter((_, i) => i !== n - 1) };
}

/** Move the bowler at place `n` by `delta` (-1 up, +1 down). Off either end stays put. */
export function moveBowler(state, n, delta) {
  const to = n - 1 + delta;
  if (!state.bowling[n - 1] || to < 0 || to >= state.bowling.length) return state;
  return { ...state, bowling: swap(state.bowling, n - 1, to) };
}

/** Set one volunteer name (`scorer`, `umpire_1` or `umpire_2`). */
export function setVolunteer(state, key, value) {
  if (!VOLUNTEER_KEYS.includes(key)) return state;
  return { ...state, volunteers: { ...state.volunteers, [key]: value } };
}

/** The `PUT /api/coach-games/:id/sheet` body. A blank volunteer is sent as null. */
export function toBody(state) {
  const name = (v) => {
    const t = (v || "").trim();
    return t ? t : null;
  };
  return {
    xi: state.xi.map((e, i) => ({ player_id: e.id, bat_order: i + 1, captain: e.captain, vice_captain: e.vice, keeper: e.keeper })),
    twelfth: state.twelfth,
    bowling: [...state.bowling],
    volunteers: { scorer: name(state.volunteers.scorer), umpire_1: name(state.volunteers.umpire_1), umpire_2: name(state.volunteers.umpire_2) },
  };
}

/** What the "Picked N of 11" card counts. Nothing here blocks Next: it is the warning, not a gate. */
export function checklist(state) {
  return {
    picked: state.xi.length,
    of: XI_SIZE,
    captain: state.xi.some((e) => e.captain),
    vice: state.xi.some((e) => e.vice),
    keeper: state.xi.some((e) => e.keeper),
    bowlers: state.bowling.length,
  };
}
